/**
 * W4 — the shapes of the half of the product that exists because a **VS Code window is a
 * first-class actor in the workspace**: connected editor windows, the work dispatched into
 * them, the scoped grants that let a colleague reach one, the markdown folders a clone keeps
 * in step with a collection, and the conversations a remote session is held in.
 *
 * Envelope discipline in this wave is deliberately NOT uniform, so every family is pinned by a
 * golden fixture rather than assumed (`knowitIde.test.ts`, `knowitDocSync.test.ts`):
 *
 * | family              | envelope                                                    |
 * |---------------------|-------------------------------------------------------------|
 * | `ide-sessions.*`    | `{data}` (+ `{policy}` on `.list`)                           |
 * | `ide-access.*`      | `{data}`                                                     |
 * | `ai-dispatch.*`     | bare, ad-hoc — `{ok, …}`, `{dispatches, policy}`, `{ok, dispatch}` |
 * | `doc-sync/*`        | bare typed records, over REST **GET**s                       |
 * | `document-conversations` | bare — `.list` is a bare ARRAY                          |
 * | `wizard.*`          | bare                                                         |
 *
 * The two status vocabularies are kept apart on purpose (§E-6): `status` is the PascalCase
 * lifecycle enum, lower-cased here by slate convention; `statusDetail` is the server's own
 * person-facing projection and is kept **verbatim**, because `awaiting-approval` and
 * `unreachable-window` have no equivalent in `status` at all.
 */
import { z } from "zod";
import { IdeSessionSchema } from "~/api/opsTypes";

// ── Sessions ────────────────────────────────────────────────────────────────

/**
 * `ide-sessions.list` answers `{data, policy}`: the rows, plus whether this viewer is seeing
 * the whole workspace's windows and what extension version the server advises. Both belong
 * to the page rather than to a row — a per-row copy would be the same value repeated N times
 * and could disagree with itself.
 */
export const IdeSessionsPageSchema = z.object({
  sessions: z.array(IdeSessionSchema),
  /** `policy.admin` — "you are seeing everyone's windows", not a permission to act. */
  admin: z.boolean().default(false),
  /** `Ide:MinimumAdvisedExtensionVersion`; null when the server has no opinion. */
  minimumAdvisedExtensionVersion: z.string().nullable().default(null),
});
export type IdeSessionsPage = z.infer<typeof IdeSessionsPageSchema>;

export const IDE_HISTORY_KINDS = ["dispatch", "run", "action"] as const;
export type IdeHistoryKind = (typeof IDE_HISTORY_KINDS)[number];

/**
 * One row of a window's five-band history. `title` is **server-authored English** ("Asked
 * another window", the run's name, the tool's name) — it is data, and `t()` on a server string
 * would be a lie, so nothing translates it.
 */
export const IdeHistoryItemSchema = z.object({
  at: z.string(),
  kind: z.enum(IDE_HISTORY_KINDS),
  direction: z.enum(["in", "out"]),
  title: z.string(),
  /** Null for a colleague's window — the server redacts, and `policy.bodiesVisible` says so. */
  detail: z.string().nullable().default(null),
  body: z.string().nullable().default(null),
  status: z.string().nullable().default(null),
  latencyMs: z.number().nullable().default(null),
  tokens: z.number().nullable().default(null),
  costMicros: z.number().nullable().default(null),
  refs: z.record(z.string(), z.string().nullable()).default({}),
});
export type IdeHistoryItem = z.infer<typeof IdeHistoryItemSchema>;

export const IdeSpendSchema = z.object({
  calls: z.number().default(0),
  inputTokens: z.number().default(0),
  outputTokens: z.number().default(0),
  costMicros: z.number().default(0),
});
export type IdeSpend = z.infer<typeof IdeSpendSchema>;

export const IdeHealthSchema = z.object({
  lastSeenAt: z.string().nullable().default(null),
  heartbeatGapSeconds: z.number().nullable().default(null),
  isOnline: z.boolean().default(false),
  revokedAt: z.string().nullable().default(null),
  offlineAnnouncedAt: z.string().nullable().default(null),
  /** The declared `ide.inbound.<policy>` capability, or null when the window declared none. */
  inboundPolicy: z.string().nullable().default(null),
});
export type IdeHealth = z.infer<typeof IdeHealthSchema>;

export const IdeHistoryPolicySchema = z.object({
  isOwn: z.boolean().default(false),
  admin: z.boolean().default(false),
  /**
   * The redaction contract, stated by the server rather than inferred: an Admin reading a
   * colleague's window sees THAT work happened, never WHAT was said.
   */
  bodiesVisible: z.boolean().default(false),
});
export type IdeHistoryPolicy = z.infer<typeof IdeHistoryPolicySchema>;

/** Five bands over ONE request — a console that needs six round trips does not survive. */
export const IdeSessionHistorySchema = z.object({
  session: IdeSessionSchema,
  items: z.array(IdeHistoryItemSchema).default([]),
  spend: IdeSpendSchema,
  health: IdeHealthSchema,
  policy: IdeHistoryPolicySchema,
});
export type IdeSessionHistory = z.infer<typeof IdeSessionHistorySchema>;

// ── Dispatch (the writes; the LIST stays on `automation.dispatches`) ─────────

export const DISPATCH_KINDS = ["flow", "chat", "thought"] as const;
export type DispatchKind = (typeof DISPATCH_KINDS)[number];

/**
 * What the web may ask of a window.
 *
 * `originSessionKey` is deliberately absent (D9): a web caller has no `process.cwd()`, so the
 * server would drop it anyway, and a site-launched dispatch legitimately appears in no
 * window's outbound history. `permissionMode`/`allowedTools`/`disallowedTools` are absent for
 * a stronger reason (D2) — permission is the RECEIVER's setting, the server strips them from
 * the top level of the payload, and offering them here would advertise a control slate does
 * not have.
 */
export interface DispatchDraft {
  kind: DispatchKind;
  /** Defaults to `ClaudeCode` server-side; sent explicitly so the row reads honestly. */
  agent?: string;
  payload: Record<string, unknown>;
  /** One window, and it must be one of the actor's own — cross-person needs a live grant. */
  targetSessionKey?: string;
  conversationId?: string;
  model?: string;
  /** Reasoning effort (`--effort`): low | medium | high | xhigh | max. Costs; never grants. */
  effort?: string;
  tokenBudget?: number;
  /** Clamped to 3600 server-side; `AiDispatch.DefaultTtl` is 10 minutes. */
  ttlSeconds?: number;
}

export const DispatchCreatedSchema = z.object({
  id: z.string(),
  /** The grant that admitted it, when it was cross-person. Null for one's own window. */
  grantId: z.string().nullable().default(null),
  status: z.string().default("queued"),
  /** `pushed` = a live socket took it; `queued` = the window will poll it up within 5 s. */
  delivery: z.string().default("queued"),
  expiresAt: z.string().nullable().default(null),
  agent: z.string().default(""),
  telemetry: z.string().default("full"),
  telemetryNote: z.string().nullable().default(null),
});
export type DispatchCreated = z.infer<typeof DispatchCreatedSchema>;

/**
 * `ai-dispatch.threads` — which window one of the caller's conversations is held WITH.
 *
 * The window is named by its durable coordinate (`machineId` + `workspacePath`), not by the
 * session key alone: a key is minted per activation, so the key of yesterday's turn names no
 * window that exists today, while the folder on that machine still does. Somebody else's
 * window (a turn sent under a grant) keeps its key and has no place.
 */
export const TalkThreadSchema = z.object({
  conversationId: z.string(),
  turns: z.number().default(0),
  firstAt: z.string().nullable().default(null),
  lastAt: z.string().nullable().default(null),
  /** The lifecycle word of the LATEST turn, lower-cased. */
  lastStatus: z.string().default(""),
  /** The latest turn is still queued, claimed or running. */
  live: z.boolean().default(false),
  /** What the latest turn asked the window for; null = the window's own default. */
  model: z.string().nullable().default(null),
  effort: z.string().nullable().default(null),
  sessionKey: z.string().nullable().default(null),
  isOwnWindow: z.boolean().default(false),
  machineId: z.string().nullable().default(null),
  machineName: z.string().nullable().default(null),
  workspacePath: z.string().nullable().default(null),
  workspaceName: z.string().nullable().default(null),
});
export type TalkThread = z.infer<typeof TalkThreadSchema>;

/**
 * `ai-dispatch.output` — the lines a window wrote for a dispatch, read back by its SENDER only.
 *
 * A dispatch's `result` is clipped at 4,000 characters; an answer longer than that (a window's
 * Claude Code sessions, a transcript, a summary) travels as the dispatch's lines, and the result
 * carries only its shape. `kind` is the window's word: `content` (one item of the answer),
 * `result` (the answer itself), `status`.
 */
export const DispatchOutputLineSchema = z.object({
  seq: z.number(),
  kind: z.string().default("content"),
  text: z.string().default(""),
  at: z.string().nullable().default(null),
});
export type DispatchOutputLine = z.infer<typeof DispatchOutputLineSchema>;

export const DispatchOutputSchema = z.object({
  /** The lifecycle word, lower-cased. */
  status: z.string().default("queued"),
  result: z.string().nullable().default(null),
  error: z.string().nullable().default(null),
  lines: z.array(DispatchOutputLineSchema).default([]),
  hasMore: z.boolean().default(false),
});
export type DispatchOutput = z.infer<typeof DispatchOutputSchema>;

/**
 * The eight words `statusDetail` can be, plus the fallback. `status` cannot say
 * `awaiting-approval`, `unreachable-window` or `declined` at all, which is the entire reason
 * this vocabulary exists — and why an unknown word must render as a neutral pill rather than
 * blank the row (§E-6).
 */
export const DISPATCH_STATUS_DETAILS = [
  "queued",
  "unreachable-window",
  "awaiting-approval",
  "running",
  "completed",
  "failed",
  "expired",
  "cancelled",
  "declined",
] as const;
export type DispatchStatusDetail = (typeof DISPATCH_STATUS_DETAILS)[number];

// ── Grants ──────────────────────────────────────────────────────────────────

export const IDE_GRANT_LEVELS = ["read", "write", "flow-run"] as const;
export type IdeGrantLevel = (typeof IDE_GRANT_LEVELS)[number];

export const IDE_GRANT_STATUSES = ["pending", "active", "declined", "revoked", "expired"] as const;
export type IdeGrantStatus = (typeof IDE_GRANT_STATUSES)[number];

export const IdeGrantScopeSchema = z.object({
  machineId: z.string().nullable().default(null),
  workspacePathPrefix: z.string().nullable().default(null),
});
export type IdeGrantScope = z.infer<typeof IdeGrantScopeSchema>;

export const IdeGrantSchema = z.object({
  id: z.string(),
  ownerUserId: z.string(),
  ownerUserName: z.string().nullable().default(null),
  granteeUserId: z.string(),
  granteeUserName: z.string().nullable().default(null),
  status: z.string(),
  /**
   * **Null unless the grant is active** — and deliberately not defaulted to `"read"`. A
   * pending request has no level yet; showing one would state a decision nobody made.
   */
  level: z.string().nullable().default(null),
  scope: IdeGrantScopeSchema.default({ machineId: null, workspacePathPrefix: null }),
  purpose: z.string().default(""),
  contextDocumentId: z.string().nullable().default(null),
  contextConversationId: z.string().nullable().default(null),
  createdAt: z.string(),
  approvedAt: z.string().nullable().default(null),
  declinedAt: z.string().nullable().default(null),
  revokedAt: z.string().nullable().default(null),
  expiresAt: z.string().nullable().default(null),
  lastUsedAt: z.string().nullable().default(null),
  /** Read-only in W4 — `.set-always` is the extension's affordance, not the web's (§E-8.8). */
  alwaysAllow: z.boolean().default(false),
  threadAllowances: z
    .array(z.object({ threadKey: z.string(), expiresAt: z.string().nullable().default(null) }))
    .default([]),
  /** SUM(billing_usage.quantity) for `ai.tokens.in|out` rows carrying this grant. */
  causedSpendTokens: z.number().default(0),
});
export type IdeGrant = z.infer<typeof IdeGrantSchema>;

export const IdeGrantListSchema = z.object({
  asOwner: z.array(IdeGrantSchema).default([]),
  asGrantee: z.array(IdeGrantSchema).default([]),
});
export type IdeGrantList = z.infer<typeof IdeGrantListSchema>;

export interface IdeGrantApproval {
  grantId: string;
  level: IdeGrantLevel;
  scope?: { machineId?: string; workspacePathPrefix?: string };
  expiresAt?: string;
}

/**
 * **Existence level only** by construction: `request-candidates` never names a machine, a
 * workspace or a path — asking somebody for access must not itself be reconnaissance.
 */
export const IdeGrantCandidateSchema = z.object({
  userId: z.string(),
  userName: z.string().nullable().default(null),
  onlineWindowCount: z.number().default(0),
});
export type IdeGrantCandidate = z.infer<typeof IdeGrantCandidateSchema>;

/**
 * The per-use trail. The contract is live and the trail is **empty by construction** until
 * `AiAction.GrantId` ships — which is why the surface must say "records land when the audit
 * column ships" rather than "no activity" (§E-8.7). Left untyped: there is no shape to pin yet.
 */
export type IdeGrantActivity = Record<string, unknown>;

// ── Documentation sync ──────────────────────────────────────────────────────

/**
 * One clone. `lastPullAt` is touched **only by `/pull-ack`, never by a poll** — a client that
 * polls but fails to apply must not look healthy, which is exactly the shape of the
 * 2026-08-09 silent-drift incident (§E-1).
 */
export const DocSyncClientSchema = z.object({
  machineId: z.string().default(""),
  workspacePath: z.string().default(""),
  lastPushAt: z.string().nullable().default(null),
  lastPullAt: z.string().nullable().default(null),
  lastAckCursor: z.number().default(0),
  extensionVersion: z.string().nullable().default(null),
});
export type DocSyncClient = z.infer<typeof DocSyncClientSchema>;

/** `new` is `[JsonPropertyName("new")]` on the wire — the key survives verbatim. */
export const DocSyncCountsSchema = z.object({
  new: z.number().default(0),
  triaged: z.number().default(0),
  conflict: z.number().default(0),
  collab: z.number().default(0),
});
export type DocSyncCounts = z.infer<typeof DocSyncCountsSchema>;

export const DocSyncLinkSchema = z.object({
  linkId: z.string(),
  collectionId: z.string().default(""),
  parentDocumentId: z.string().nullable().default(null),
  syncPath: z.string().default(""),
  gitUrl: z.string().nullable().default(null),
  enabled: z.boolean().default(false),
  autoRunFlow: z.boolean().default(false),
  /** The entire early-warning system for silent drift. Never hide it, never round it away. */
  lastSyncAt: z.string().nullable().default(null),
  counts: DocSyncCountsSchema.default({ new: 0, triaged: 0, conflict: 0, collab: 0 }),
  pullEnabled: z.boolean().default(false),
  pullHead: z.number().default(0),
  workspaceName: z.string().nullable().default(null),
  projectId: z.string().nullable().default(null),
  projectName: z.string().nullable().default(null),
  collectionName: z.string().nullable().default(null),
  collectionUrl: z.string().nullable().default(null),
  parentDocumentTitle: z.string().nullable().default(null),
  parentDocumentUrl: z.string().nullable().default(null),
  /** Capped at 20 server-side, newest activity first. */
  clients: z.array(DocSyncClientSchema).default([]),
});
export type DocSyncLink = z.infer<typeof DocSyncLinkSchema>;

/**
 * The only two flags the web writes. `null` means KEEP server-side, which is why the adapter
 * echoes the whole link back with one flag changed rather than posting a bare patch: sending
 * `false` for a field nobody touched would turn it off.
 */
export interface DocSyncLinkFlags {
  enabled?: boolean;
  pullEnabled?: boolean;
}

export const DOC_SYNC_STATES = ["new", "triaged", "conflict", "collab"] as const;

export const DocSyncEntrySchema = z.object({
  relPath: z.string().default(""),
  documentId: z.string().nullable().default(null),
  lastSyncHash: z.string().nullable().default(null),
  state: z.string().default("new"),
  updatedAt: z.string().nullable().default(null),
});
export type DocSyncEntry = z.infer<typeof DocSyncEntrySchema>;

export const DocSyncStatePageSchema = z.object({
  entries: z.array(DocSyncEntrySchema).default([]),
  counts: DocSyncCountsSchema.default({ new: 0, triaged: 0, conflict: 0, collab: 0 }),
});
export type DocSyncStatePage = z.infer<typeof DocSyncStatePageSchema>;

// ── Conversations (the remote-session transcript store) ─────────────────────

export const ConversationSummarySchema = z.object({
  id: z.string(),
  documentId: z.string().nullable().default(null),
  documentTitle: z.string().nullable().default(null),
  title: z.string().default(""),
  status: z.string().nullable().default(null),
  messageCount: z.number().default(0),
  // The lifecycle face sends the whole last MESSAGE OBJECT, not a string (W8a finding); W4
  // consumers only need a preview, so coerce an object to its text and keep the string type.
  lastMessage: z
    .preprocess((value) => {
      if (value && typeof value === "object") {
        const m = value as Record<string, unknown>;
        const text = m.content ?? m.text ?? m.body;
        return typeof text === "string" ? text : null;
      }
      return value;
    }, z.string().nullable())
    .default(null),
  flowId: z.string().nullable().default(null),
  flowName: z.string().nullable().default(null),
  createdAt: z.string().nullable().default(null),
  updatedAt: z.string().nullable().default(null),
});
export type ConversationSummary = z.infer<typeof ConversationSummarySchema>;

/**
 * One row satisfies both readers — the role-shaped one and the thoughts-shaped one — because
 * the server emits both field sets. Only what the transcript renders is modelled here.
 */
export const ConversationMessageSchema = z.object({
  id: z.string(),
  role: z.string().default("assistant"),
  content: z.string().default(""),
  createdAt: z.string().nullable().default(null),
  thinkerId: z.string().nullable().default(null),
  author: z.string().nullable().default(null),
});
export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;

export const ConversationDetailSchema = ConversationSummarySchema.extend({
  messages: z.array(ConversationMessageSchema).default([]),
});
export type ConversationDetail = z.infer<typeof ConversationDetailSchema>;

export interface ConversationDraft {
  title: string;
  documentId?: string;
  anchorType?: string;
  flowId?: string;
}

// ── First-run wizard ────────────────────────────────────────────────────────

/**
 * `compose` is server-side ROUTE DISCOVERY — the server scans its own action-descriptor table
 * for a `v2/documents` + `sculpt` template. "Never fake it": a wizard that offers composition
 * the server cannot perform is worse than one that offers naming.
 */
export const WizardCapabilitiesSchema = z.object({
  wizard: z.boolean().default(true),
  compose: z.boolean().default(false),
});
export type WizardCapabilities = z.infer<typeof WizardCapabilitiesSchema>;

/**
 * Step 2's preflight. Two independent probes, each with its own 5 s timeout, and `operable` is
 * their OR — a workspace with a reachable local model needs no cloud catalogue, and vice
 * versa. A timeout is data (`reachable:false`), never an exception: "we could not tell" and
 * "it is broken" must reach the same calm panel.
 */
export interface WizardPreflight {
  operable: boolean;
  catalog: { status: string; count: number };
  local: { reachable: boolean; models: string[] };
}

export const SeedWelcomeSchema = z.object({
  ok: z.boolean().default(false),
  created: z.boolean().default(false),
  documentId: z.string().nullable().default(null),
  url: z.string().nullable().default(null),
  archived: z.boolean().nullable().default(null),
});
export type SeedWelcome = z.infer<typeof SeedWelcomeSchema>;

// ── Shared pure helpers (owner logic; the ones a rewrite gets wrong) ─────────

/**
 * The dispatch payload is a raw JSON **string**, and it is not always JSON (§E-12). The
 * server's own timeline reaches for these five keys in this order and tolerates failure; so
 * does this. An unreadable payload must never blank a row.
 */
export function promptFromPayload(payload: string | null | undefined): string {
  if (!payload) {
    return "";
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    return "";
  }
  if (!parsed || typeof parsed !== "object") {
    return "";
  }
  const record = parsed as Record<string, unknown>;
  for (const key of ["prompt", "text", "instruction", "message", "flowId"]) {
    const value = record[key];
    if (typeof value === "string" && value.length > 0) {
      return value;
    }
  }
  return "";
}
